import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SetError } from '@src/redux/modals';
import { AdminLambdaFetch, fetchWithCometFallback } from '@src/utils/fetch-utils';
import {
  BY_COMPANY_KEY,
  BY_NETWORK_KEY,
  PLATFORM_DISPLAY,
  PacingData,
  PacingFetchParams,
  PacingSubData,
  USE_NETWORK_GROUP_KEY,
  USE_NETWORK_KEY
} from './streaming-pacing-types';
import { defaultDates, pick } from './streaming-pacing-utils';

export const FETCH_FAILED_TITLE = 'Unable to load pacing data';

const SUB_KEYS = ['company', 'network', 'description', 'platform'] as const;
const TOP_KEYS = [...SUB_KEYS, 'audio_buy_type'] as const;

// Long date ranges outrun the API Gateway timeout, so those fall back to comet loading.
const fetchPacing = <T>(params: PacingFetchParams): Promise<T> =>
  fetchWithCometFallback<PacingFetchParams, T>({
    service: AdminLambdaFetch,
    standardEndpoint: '/pacing',
    input: params
  });

export const useStreamingPacing = (globalPlatform: string, setError: SetError) => {
  const [dates, setDates] = useState(() => defaultDates(globalPlatform));
  const [grouping, setGrouping] = useState<string>(BY_COMPANY_KEY);
  const [key, setKey] = useState<string | undefined>();
  const [subKey, setSubKey] = useState<string | undefined>();
  const [useNetworkGroup, setUseNetworkGroup] = useState<string>(
    globalPlatform === PLATFORM_DISPLAY ? USE_NETWORK_GROUP_KEY : USE_NETWORK_KEY
  );
  const { start, end } = dates;
  const networkGroupParam = useNetworkGroup === USE_NETWORK_GROUP_KEY ? 1 : 0;
  const queryKey = ['streamingPacing', globalPlatform, networkGroupParam, grouping, start, end];

  // Each level is its own query, so a slow response can only fill in its own level.
  const topQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey,
    queryFn: () =>
      fetchPacing<PacingData[]>({
        ...dates,
        groupByNetwork: grouping === BY_NETWORK_KEY ? 1 : 0,
        globalPlatform,
        useNetworkGroup: networkGroupParam
      }),
    enabled: Boolean(start && end)
  });
  const foundData = key ? topQuery.data?.find(elem => elem.key === key) : undefined;

  const subQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: [...queryKey, key],
    queryFn: () => {
      const params: PacingFetchParams = {
        ...dates,
        globalPlatform,
        ...pick(foundData, TOP_KEYS),
        useNetworkGroup: networkGroupParam
      };
      if (grouping === BY_COMPANY_KEY) {
        params.company = key;
      } else {
        params.groupByNetwork = 1;
      }
      if (networkGroupParam) {
        delete params.description;
      }
      return fetchPacing<PacingSubData[]>(params);
    },
    enabled: Boolean(foundData)
  });
  const foundSubData = subKey ? subQuery.data?.find(elem => elem.key === subKey) : undefined;

  const daysQuery = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: [...queryKey, key, subKey],
    queryFn: () => {
      const params: PacingFetchParams = {
        ...dates,
        globalPlatform,
        ...pick(foundData, SUB_KEYS),
        ...pick(foundSubData, SUB_KEYS),
        useNetworkGroup: networkGroupParam
      };
      if (networkGroupParam) {
        delete params.description;
      }
      return fetchPacing<PacingSubData['days']>(params);
    },
    enabled: Boolean(foundData && foundSubData)
  });

  // Slots the lazily-fetched levels into the top level data the page reads.
  const data = useMemo(
    () =>
      topQuery.data?.map(elem =>
        elem.key === key && subQuery.data
          ? {
              ...elem,
              subData: subQuery.data.map(sub =>
                sub.key === subKey && daysQuery.data ? { ...sub, days: daysQuery.data } : sub
              )
            }
          : elem
      ),
    [daysQuery.data, key, subKey, subQuery.data, topQuery.data]
  );

  const error = topQuery.error ?? subQuery.error ?? daysQuery.error;

  useEffect(() => {
    if (error) {
      setError({ message: error.message, title: FETCH_FAILED_TITLE, reportError: error });
    }
  }, [error, setError]);

  useEffect(() => {
    if (key && topQuery.data && !foundData) {
      setError({
        message: `We don't have any data for key: ${key}. Please select another option!`,
        title: 'No Data found'
      });
      setKey(undefined);
    }
  }, [foundData, key, setError, topQuery.data]);

  useEffect(() => {
    if (key && subKey && subQuery.data && !foundSubData) {
      setError({
        message: `We don't have any data for key: ${key} and subKey: ${subKey}. Please select another option!`,
        title: 'No Data found'
      });
      setSubKey(undefined);
    }
  }, [foundSubData, key, setError, subKey, subQuery.data]);

  return {
    // The top level query stays on its own cached data while a drill-down loads, so the panes
    // don't unmount.
    data,
    dates,
    fetchFailed: Boolean(error),
    grouping,
    key,
    setDates,
    setGrouping,
    setKey,
    setSubKey,
    setUseNetworkGroup,
    subKey,
    useNetworkGroup
  };
};
